import { useState } from 'react';
import './UpgradeHotspot.css';

/**
 * UpgradeHotspot - A pulsing indicator that shows upgrade prompts for locked features
 * 
 * @param {string} feature - Name of the locked feature
 * @param {string} description - Description of what upgrading unlocks
 * @param {string} currentTier - User's current tier (recruit, sergeant, lieutenant)
 * @param {string} requiredTier - Minimum tier required for this feature
 * @param {function} onUpgrade - Callback when user clicks upgrade
 * @param {string} position - Position of hotspot (top-right, top-left, inline)
 */
const UpgradeHotspot = ({ 
  feature, 
  description, 
  currentTier = 'recruit',
  requiredTier = 'sergeant',
  onUpgrade,
  position = 'top-right',
  children 
}) => {
  const [showPopup, setShowPopup] = useState(false);

  const tierHierarchy = {
    'free': 0,
    'recruit': 0,
    'sergeant': 1,
    'monthly': 1,
    'lieutenant': 2,
    'yearly': 2
  };

  const tierNames = {
    'free': 'Recruit',
    'recruit': 'Recruit',
    'sergeant': 'Sergeant',
    'monthly': 'Sergeant',
    'lieutenant': 'Lieutenant',
    'yearly': 'Lieutenant'
  };

  const tierIcons = {
    'recruit': '🎖️',
    'sergeant': '⭐',
    'lieutenant': '🏅'
  };

  const isLocked = tierHierarchy[currentTier] < tierHierarchy[requiredTier];

  if (!isLocked) {
    return children || null;
  }

  return (
    <div className={`upgrade-hotspot-wrapper ${position}`}>
      {children}
      <button 
        className="upgrade-hotspot-trigger"
        onClick={() => setShowPopup(true)}
        aria-label={`Upgrade to unlock ${feature}`}
        data-testid="upgrade-hotspot"
      >
        <span className="hotspot-pulse"></span>
        <span className="hotspot-icon">🔒</span>
      </button>

      {showPopup && (
        <div className="upgrade-popup-overlay" onClick={() => setShowPopup(false)}>
          <div className="upgrade-popup" onClick={(e) => e.stopPropagation()}>
            <button 
              className="upgrade-popup-close" 
              onClick={() => setShowPopup(false)}
              aria-label="Close"
            >
              ✕
            </button>
            
            <div className="upgrade-popup-header">
              <span className="upgrade-popup-lock">🔒</span>
              <h3>Feature Locked</h3>
            </div>
            
            <div className="upgrade-popup-content">
              <p className="upgrade-popup-feature">{feature}</p>
              <p className="upgrade-popup-description">{description}</p>
              
              <div className="upgrade-popup-tiers">
                <div className="tier-current">
                  <span className="tier-label">Your Tier</span>
                  <span className="tier-name">
                    {tierIcons[currentTier] || '🎖️'} {tierNames[currentTier] || 'Recruit'}
                  </span>
                </div>
                <div className="tier-arrow">→</div>
                <div className="tier-required">
                  <span className="tier-label">Required</span>
                  <span className="tier-name">
                    {tierIcons[requiredTier] || '⭐'} {tierNames[requiredTier] || 'Sergeant'}+
                  </span>
                </div>
              </div>
            </div>
            
            <div className="upgrade-popup-actions">
              <button 
                className="upgrade-popup-btn primary"
                onClick={() => {
                  setShowPopup(false);
                  onUpgrade && onUpgrade();
                }}
                data-testid="upgrade-popup-btn"
              >
                ⬆️ Upgrade Now
              </button>
              <button 
                className="upgrade-popup-btn secondary"
                onClick={() => setShowPopup(false)}
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeHotspot;
